/**
 * Room generator — the reference compound generator.
 *
 * Emits a group with a floor, four walls, and an optional ceiling. The
 * parent SceneObject carries no geometry of its own; it's a transform
 * container. Each child carries axis-aligned box geometry sized for its
 * role and positioned via its `localTransform`.
 *
 * Coordinate convention (Z-up, matching the rest of PoseRef):
 *   X = width, Y = depth, Z = height
 *
 * Local anchor: the parent's local origin sits at the floor's geometric
 * centre at Z = 0. The floor sits *below* Z = 0 (its top coincides with
 * the origin); walls rise from Z = 0 up to Z = height; ceiling sits
 * *above* Z = height.
 *
 * Details (doorways, windows) are a `GeneratorListParamSpec` — each item
 * names a target wall and parameters that describe a rectangular hole
 * in that wall's face. The wall's geometry is computed by 2D rectangle
 * difference (start with one rect covering the full face, subtract each
 * hole) and each resulting rectangle becomes an axis-aligned box segment.
 * The segments for one wall are concatenated into a single mesh so the
 * wall remains one SceneObject across rebuilds — preserving its role,
 * selection, and materials — even as doorways/windows are added/removed.
 */

import type { PrimitiveGeometry } from "../primitives.ts";
import type { BoxSpec } from "./generatorGeom.ts";
import { buildBox, concatBoxes } from "./generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorChild,
  GeneratorHideablePart,
  GeneratorListItem,
  GeneratorListItemType,
  GeneratorLocalTransform,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  boolParam,
  defineGenerator,
  enumParam,
  intParam,
  listParam,
  numParam,
} from "./types.ts";

interface RoomParams extends GeneratorParams {
  width: number;
  depth: number;
  height: number;
  wallThickness: number;
  hasCeiling: boolean;
  hasCrownMolding: boolean;
  crownMoldingSize: number;
  hasBaseboard: boolean;
  baseboardHeight: number;
  baseboardThickness: number;
  details: readonly GeneratorListItem[];
}

// ---------------------------------------------------------------------------
// Wall roles + tangent axis
// ---------------------------------------------------------------------------

const WALL_ROLES = ["wall_n", "wall_s", "wall_e", "wall_w"] as const;
type WallRole = (typeof WALL_ROLES)[number];

function isWallRole(role: string): role is WallRole {
  return (WALL_ROLES as readonly string[]).includes(role);
}

/** Wall tangent (along-length) axis — X for N/S walls, Y for E/W walls. */
type WallTangent = "x" | "y";

function wallTangent(role: WallRole): WallTangent {
  return role === "wall_n" || role === "wall_s" ? "x" : "y";
}

/**
 * Expand a detail's id across its `count` — mirrors the array expansion in
 * `build()` exactly (`id` → `id_0 … id_{n-1}`). Kept as a tiny standalone
 * helper so `dependentRoles` can enumerate sub-role ids without re-running
 * the full build's position math.
 */
function expandDetailIds(detail: GeneratorListItem): readonly string[] {
  const count = intParam(detail.params, "count", 1);
  if (count <= 1) return [detail.id];
  return Array.from({ length: count }, (_, i) => `${detail.id}_${i}`);
}

/**
 * Roles that share a wall's visibility — crown, baseboard, and every
 * door/window sub-role for details parented to this wall. Emits the full
 * set of *possible* detail sub-roles (panel, frame, pane) regardless of
 * whether each is currently generated; the panel skips roles that aren't
 * in the live `roleToObjectId`, so no toggle ever points at a ghost.
 */
function wallDependentRoles(wall: WallRole) {
  const suffix = wall.slice(5); // "n" | "s" | "e" | "w"
  return (params: GeneratorParams): readonly string[] => {
    const roles: string[] = [];
    if (boolParam(params, "hasCrownMolding", false))
      roles.push(`crown_${suffix}`);
    if (boolParam(params, "hasBaseboard", false)) roles.push(`base_${suffix}`);
    for (const detail of listParam(params, "details")) {
      if (enumParam(detail.params, "wall", "wall_n") !== wall) continue;
      for (const id of expandDetailIds(detail)) {
        if (detail.itemType === "doorway") {
          roles.push(`door_panel_${id}`, `door_frame_${id}`);
        } else if (detail.itemType === "window") {
          roles.push(`window_frame_${id}`, `window_pane_${id}`);
        }
      }
    }
    return roles;
  };
}

const ROOM_HIDEABLE_PARTS: readonly GeneratorHideablePart[] = WALL_ROLES.map(
  (role) => ({
    roleId: role,
    label: `Wall ${role.slice(5).toUpperCase()}`,
    dependentRoles: wallDependentRoles(role),
  }),
);

// ---------------------------------------------------------------------------
// 2D rectangle difference — the core of wall-with-holes segmentation
// ---------------------------------------------------------------------------

interface Rect2 {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/**
 * Subtract an axis-aligned hole from a set of non-overlapping rectangles.
 * The result still tiles the same region minus the hole, using up to four
 * surrounding rects per input (bottom / top / left / right of the hole).
 *
 * Called iteratively with each hole, starting from a single wall-face rect.
 * Runs in O(n) per hole where n is the current rect count — trivially fast
 * at the scale a single room reaches (a handful of openings per wall).
 */
function subtractHole(rects: readonly Rect2[], hole: Rect2): Rect2[] {
  const out: Rect2[] = [];
  for (const r of rects) {
    const ix0 = Math.max(hole.x0, r.x0);
    const iy0 = Math.max(hole.y0, r.y0);
    const ix1 = Math.min(hole.x1, r.x1);
    const iy1 = Math.min(hole.y1, r.y1);
    if (ix0 >= ix1 || iy0 >= iy1) {
      out.push(r); // no overlap
      continue;
    }
    if (r.y0 < iy0) out.push({ x0: r.x0, y0: r.y0, x1: r.x1, y1: iy0 });
    if (r.y1 > iy1) out.push({ x0: r.x0, y0: iy1, x1: r.x1, y1: r.y1 });
    if (r.x0 < ix0) out.push({ x0: r.x0, y0: iy0, x1: ix0, y1: iy1 });
    if (r.x1 > ix1) out.push({ x0: ix1, y0: iy0, x1: r.x1, y1: iy1 });
  }
  return out;
}

/**
 * Build a wall mesh in wall-local space, with rectangular holes cut out.
 *
 * The wall is thought of as a 2D face of size `faceWidth` × `height`. The
 * caller supplies holes in those face-local coordinates (origin at the
 * bottom-left corner of the face). Each resulting rectangle becomes an
 * extruded box in the wall's 3D local space, centred about the wall's
 * own origin — so the caller can keep the wall's `localTransform`
 * unchanged whether or not there are any holes.
 */
function buildWallMesh(
  tangent: WallTangent,
  faceWidth: number,
  height: number,
  thickness: number,
  holes: readonly Rect2[],
): PrimitiveGeometry {
  let rects: Rect2[] = [{ x0: 0, y0: 0, x1: faceWidth, y1: height }];
  for (const raw of holes) {
    const clamped: Rect2 = {
      x0: Math.max(0, Math.min(faceWidth, raw.x0)),
      y0: Math.max(0, Math.min(height, raw.y0)),
      x1: Math.max(0, Math.min(faceWidth, raw.x1)),
      y1: Math.max(0, Math.min(height, raw.y1)),
    };
    if (clamped.x1 - clamped.x0 < 1e-6 || clamped.y1 - clamped.y0 < 1e-6)
      continue;
    rects = subtractHole(rects, clamped);
  }

  const boxes: BoxSpec[] = [];
  for (const r of rects) {
    const w = r.x1 - r.x0;
    const h = r.y1 - r.y0;
    if (w < 1e-6 || h < 1e-6) continue;
    const tCentre = (r.x0 + r.x1) * 0.5 - faceWidth * 0.5;
    const zCentre = (r.y0 + r.y1) * 0.5 - height * 0.5;
    if (tangent === "x") {
      boxes.push({
        cx: tCentre,
        cy: 0,
        cz: zCentre,
        sx: w,
        sy: thickness,
        sz: h,
      });
    } else {
      boxes.push({
        cx: 0,
        cy: tCentre,
        cz: zCentre,
        sx: thickness,
        sy: w,
        sz: h,
      });
    }
  }

  return concatBoxes(boxes);
}

// ---------------------------------------------------------------------------
// Detail item types (doorway / window) — each is a mini-schema that produces
// a rectangular hole on its target wall's face.
// ---------------------------------------------------------------------------

const WALL_OPTIONS = [
  { value: "wall_n", label: "North (+Y)" },
  { value: "wall_s", label: "South (-Y)" },
  { value: "wall_e", label: "East (+X)" },
  { value: "wall_w", label: "West (-X)" },
] as const;

/** Shared wall-selector spec — placed first so it's the most prominent row. */
const WALL_SELECTOR_SPEC: GeneratorParamSpec = {
  id: "wall",
  label: "Wall",
  type: "enum",
  default: "wall_n",
  options: WALL_OPTIONS,
};

const DOORWAY_ITEM_TYPE: GeneratorListItemType = {
  id: "doorway",
  label: "Doorway",
  defaultParams: {
    wall: "wall_n",
    position: 0.5,
    count: 1,
    gap: 0.3,
    width: 0.9,
    height: 2.1,
    hasPanel: false,
    doorThickness: 0.04,
    openAngle: 0,
    hasHandle: false,
    hasFrame: false,
    frameThickness: 0.05,
  },
  paramSpecs: [
    WALL_SELECTOR_SPEC,
    {
      id: "position",
      label: "Position",
      type: "number",
      default: 0.5,
      min: 0,
      max: 1,
      step: 0.01,
    },
    {
      id: "count",
      label: "Count",
      type: "number",
      unit: "count",
      default: 1,
      min: 1,
      max: 24,
      step: 1,
    },
    {
      id: "gap",
      label: "Gap",
      type: "number",
      unit: "meters",
      default: 0.3,
      min: 0,
      max: 5,
      step: 0.05,
      showIf: { id: "count", value: 1, negate: true },
    },
    {
      id: "width",
      label: "Width",
      type: "number",
      unit: "meters",
      default: 0.9,
      min: 0.1,
      max: 10,
      step: 0.05,
    },
    {
      id: "height",
      label: "Height",
      type: "number",
      unit: "meters",
      default: 2.1,
      min: 0.3,
      max: 10,
      step: 0.05,
    },
    {
      type: "section",
      id: "doorway-door",
      label: "Door",
      params: [
        {
          id: "hasPanel",
          label: "Door Panel",
          type: "boolean",
          default: false,
        },
        {
          id: "doorThickness",
          label: "Panel Thickness",
          type: "number",
          unit: "meters",
          default: 0.04,
          min: 0.01,
          max: 0.2,
          step: 0.005,
          showIf: { id: "hasPanel", value: true },
        },
        {
          id: "openAngle",
          label: "Open Angle",
          type: "number",
          unit: "degrees",
          default: 0,
          min: 0,
          max: 180,
          step: 1,
          showIf: { id: "hasPanel", value: true },
        },
        {
          id: "hasHandle",
          label: "Door Handle",
          type: "boolean",
          default: false,
          showIf: { id: "hasPanel", value: true },
        },
        {
          id: "hasFrame",
          label: "Door Frame",
          type: "boolean",
          default: false,
        },
        {
          id: "frameThickness",
          label: "Frame Thickness",
          type: "number",
          unit: "meters",
          default: 0.05,
          min: 0.01,
          max: 0.2,
          step: 0.005,
          showIf: { id: "hasFrame", value: true },
        },
      ],
    },
  ],
};

const PANE_STYLE_OPTIONS = [
  { value: "none", label: "None" },
  { value: "vertical", label: "Vertical" },
  { value: "horizontal", label: "Horizontal" },
  { value: "cross", label: "Cross" },
] as const;

// Residential window defaults: Ø ~1 m wide × 1.2 m tall with a 0.9 m sill.
// `verticalPosition` is a fraction of wall height; 0.6 in a 2.5 m room
// centres the opening at 1.5 m, putting the sill at 0.9 m and the head at
// 2.1 m — both standard residential construction heights.
const WINDOW_ITEM_TYPE: GeneratorListItemType = {
  id: "window",
  label: "Window",
  defaultParams: {
    wall: "wall_n",
    position: 0.5,
    count: 1,
    gap: 0.3,
    verticalPosition: 0.6,
    width: 1.0,
    height: 1.2,
    hasFrame: false,
    frameWidth: 0.05,
    hasSill: false,
    sillDepth: 0.15,
    sillThickness: 0.04,
    paneStyle: "none",
    muntinWidth: 0.04,
  },
  paramSpecs: [
    WALL_SELECTOR_SPEC,
    {
      id: "position",
      label: "Position",
      type: "number",
      default: 0.5,
      min: 0,
      max: 1,
      step: 0.01,
    },
    {
      id: "count",
      label: "Count",
      type: "number",
      unit: "count",
      default: 1,
      min: 1,
      max: 24,
      step: 1,
    },
    {
      id: "gap",
      label: "Gap",
      type: "number",
      unit: "meters",
      default: 0.3,
      min: 0,
      max: 5,
      step: 0.05,
      showIf: { id: "count", value: 1, negate: true },
    },
    {
      id: "verticalPosition",
      label: "Height",
      type: "number",
      default: 0.6,
      min: 0,
      max: 1,
      step: 0.01,
    },
    {
      id: "width",
      label: "Width",
      type: "number",
      unit: "meters",
      default: 1.0,
      min: 0.1,
      max: 10,
      step: 0.05,
    },
    {
      id: "height",
      label: "Opening",
      type: "number",
      unit: "meters",
      default: 1.2,
      min: 0.1,
      max: 10,
      step: 0.05,
    },
    {
      type: "section",
      id: "window-frame",
      label: "Frame",
      params: [
        {
          id: "hasFrame",
          label: "Window Frame",
          type: "boolean",
          default: false,
        },
        {
          id: "frameWidth",
          label: "Frame Width",
          type: "number",
          unit: "meters",
          default: 0.05,
          min: 0.01,
          max: 0.15,
          step: 0.005,
          showIf: { id: "hasFrame", value: true },
        },
        {
          id: "hasSill",
          label: "Window Sill",
          type: "boolean",
          default: false,
          showIf: { id: "hasFrame", value: true },
        },
        {
          id: "sillDepth",
          label: "Sill Depth",
          type: "number",
          unit: "meters",
          default: 0.15,
          min: 0.02,
          max: 0.4,
          step: 0.01,
          showIf: { id: "hasSill", value: true },
        },
        {
          id: "sillThickness",
          label: "Sill Thickness",
          type: "number",
          unit: "meters",
          default: 0.04,
          min: 0.01,
          max: 0.1,
          step: 0.005,
          showIf: { id: "hasSill", value: true },
        },
      ],
    },
    {
      type: "section",
      id: "window-pane",
      label: "Pane",
      params: [
        {
          id: "paneStyle",
          label: "Pane Style",
          type: "enum",
          default: "none",
          options: PANE_STYLE_OPTIONS,
        },
        {
          id: "muntinWidth",
          label: "Muntin Width",
          type: "number",
          unit: "meters",
          default: 0.04,
          min: 0.01,
          max: 0.1,
          step: 0.005,
          showIf: { id: "paneStyle", value: "none", negate: true },
        },
      ],
    },
  ],
};

const ROOM_DETAIL_TYPES = [DOORWAY_ITEM_TYPE, WINDOW_ITEM_TYPE] as const;

/**
 * Resolve one detail's parameters into a rectangular hole in its target
 * wall's face coordinates. Returns `null` if the detail references an
 * unknown wall or unknown subtype — the detail is silently skipped so
 * schema mismatches never crash the build.
 */
function holeForDetail(
  detail: GeneratorListItem,
  faceWidth: number,
  height: number,
): Rect2 | null {
  const p = detail.params;
  const clampedPos = Math.max(0, Math.min(1, numParam(p, "position", 0.5)));

  switch (detail.itemType) {
    case "doorway": {
      const w = numParam(p, "width", 0.9);
      const h = numParam(p, "height", 2.1);
      const cx = clampedPos * faceWidth;
      // Doorways always rise from the floor; clamp height to the wall.
      const top = Math.min(height, h);
      return { x0: cx - w * 0.5, y0: 0, x1: cx + w * 0.5, y1: top };
    }
    case "window": {
      const w = numParam(p, "width", 1.0);
      const h = numParam(p, "height", 1.2);
      const clampedV = Math.max(
        0,
        Math.min(1, numParam(p, "verticalPosition", 0.5)),
      );
      const cx = clampedPos * faceWidth;
      const cz = clampedV * height;
      return {
        x0: cx - w * 0.5,
        y0: cz - h * 0.5,
        x1: cx + w * 0.5,
        y1: cz + h * 0.5,
      };
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Molding helpers — return BoxSpecs in room-local space (no child offset)
// ---------------------------------------------------------------------------

/**
 * One crown-molding segment for a wall. The piece sits against the wall's
 * inner face at the ceiling line. N/S pieces span `width`; E/W pieces span
 * `innerD` and nestle between the N/S pieces at the corners (same convention
 * as the E/W walls themselves).
 */
function crownBoxSpec(
  role: WallRole,
  width: number,
  depth: number,
  innerD: number,
  height: number,
  t: number,
  cms: number,
): BoxSpec {
  switch (role) {
    case "wall_n":
      return {
        cx: 0,
        cy: depth * 0.5 - t - cms * 0.5,
        cz: height - cms * 0.5,
        sx: width,
        sy: cms,
        sz: cms,
      };
    case "wall_s":
      return {
        cx: 0,
        cy: -(depth * 0.5 - t - cms * 0.5),
        cz: height - cms * 0.5,
        sx: width,
        sy: cms,
        sz: cms,
      };
    case "wall_e":
      return {
        cx: width * 0.5 - t - cms * 0.5,
        cy: 0,
        cz: height - cms * 0.5,
        sx: cms,
        sy: innerD,
        sz: cms,
      };
    case "wall_w":
      return {
        cx: -(width * 0.5 - t - cms * 0.5),
        cy: 0,
        cz: height - cms * 0.5,
        sx: cms,
        sy: innerD,
        sz: cms,
      };
  }
}

/**
 * Baseboard segments for one wall, with doorway openings cut out. The
 * baseboard strip is [0, faceWidth] × [0, bbh] in face space; each doorway
 * hole is subtracted so the baseboard stops at the doorway edges rather than
 * running behind the opening. Same N/S-full-width / E/W-innerD corner
 * convention as crown molding.
 */
function baseboardBoxSpecs(
  role: WallRole,
  width: number,
  depth: number,
  innerD: number,
  t: number,
  bbh: number,
  bbt: number,
  wallHoles: readonly Rect2[],
): BoxSpec[] {
  const tang = wallTangent(role);
  const faceWidth = role === "wall_n" || role === "wall_s" ? width : innerD;
  const innerFace = wallInnerFaceOffset(role, width, depth, t);
  const sign = innerFace >= 0 ? 1 : -1;
  const normalCentre = innerFace - sign * bbt * 0.5;

  // Start with the full baseboard strip and subtract doorway footprints.
  let rects: Rect2[] = [{ x0: 0, y0: 0, x1: faceWidth, y1: bbh }];
  for (const hole of wallHoles) {
    const clipped: Rect2 = {
      x0: hole.x0,
      y0: Math.max(0, hole.y0),
      x1: hole.x1,
      y1: Math.min(bbh, hole.y1),
    };
    if (clipped.x1 - clipped.x0 < 1e-6 || clipped.y1 - clipped.y0 < 1e-6)
      continue;
    rects = subtractHole(rects, clipped);
  }

  const boxes: BoxSpec[] = [];
  for (const r of rects) {
    const w = r.x1 - r.x0;
    const h = r.y1 - r.y0;
    if (w < 1e-6 || h < 1e-6) continue;
    const tCentre = (r.x0 + r.x1) * 0.5 - faceWidth * 0.5;
    const zCentre = (r.y0 + r.y1) * 0.5;
    if (tang === "x") {
      boxes.push({
        cx: tCentre,
        cy: normalCentre,
        cz: zCentre,
        sx: w,
        sy: bbt,
        sz: h,
      });
    } else {
      boxes.push({
        cx: normalCentre,
        cy: tCentre,
        cz: zCentre,
        sx: bbt,
        sy: w,
        sz: h,
      });
    }
  }
  return boxes;
}

// ---------------------------------------------------------------------------
// Face-space → room-space coordinate helpers
// ---------------------------------------------------------------------------

/**
 * Convert a horizontal face-space coordinate to a room-space offset along the
 * wall's tangent axis. Face space runs 0..faceWidth left-to-right across the
 * wall face; this re-centres it to ±faceWidth/2 in room space.
 */
function faceXToRoom(faceX: number, faceWidth: number): number {
  return faceX - faceWidth * 0.5;
}

/**
 * Position of a wall's inner face along its normal axis (room-local coords).
 * For N/S walls this is the ±Y coordinate; for E/W it's the ±X coordinate.
 */
function wallInnerFaceOffset(
  role: WallRole,
  width: number,
  depth: number,
  t: number,
): number {
  switch (role) {
    case "wall_n":
      return depth * 0.5 - t;
    case "wall_s":
      return -(depth * 0.5 - t);
    case "wall_e":
      return width * 0.5 - t;
    case "wall_w":
      return -(width * 0.5 - t);
  }
}

/**
 * Build a room-space BoxSpec from face-space coordinates. The box spans
 * [fx0, fx1] × [fz0, fz1] on the face and is extruded `depth` units toward
 * the room interior along the wall normal.
 *
 * `innerFace` is the room-space normal-axis position of the inner wall face
 * (from `wallInnerFaceOffset`). The box is placed against that face with its
 * depth projecting into the room.
 */
function faceBoxSpec(
  tangent: WallTangent,
  innerFace: number,
  faceWidth: number,
  fx0: number,
  fx1: number,
  fz0: number,
  fz1: number,
  depth: number,
): BoxSpec {
  const ftx = (fx0 + fx1) * 0.5;
  const ftz = (fz0 + fz1) * 0.5;
  const tw = fx1 - fx0;
  const th = fz1 - fz0;
  const tCentre = faceXToRoom(ftx, faceWidth);
  // Inner face is at +innerFace; project inward (sign of innerFace gives direction).
  const sign = innerFace >= 0 ? 1 : -1;
  const normalCentre = innerFace - sign * depth * 0.5;
  if (tangent === "x") {
    return {
      cx: tCentre,
      cy: normalCentre,
      cz: ftz,
      sx: tw,
      sy: depth,
      sz: th,
    };
  } else {
    return {
      cx: normalCentre,
      cy: tCentre,
      cz: ftz,
      sx: depth,
      sy: tw,
      sz: th,
    };
  }
}

// ---------------------------------------------------------------------------
// Door panel helpers
// ---------------------------------------------------------------------------

/**
 * BoxSpec for the door panel slab in child-local space. The hinge edge sits
 * at the child origin (0,0,0); the panel extends along the tangent axis by
 * `dw` and into the room by `dt`.
 *
 *   • tangent axis: 0 → dw  (door width, room X for N/S, room Y for E/W)
 *   • normal axis:  0 → ±dt (sign per wall — into the room)
 *   • Z:            0 → dh  (door height, from floor)
 */
function doorPanelBoxSpec(
  role: WallRole,
  dw: number,
  dt: number,
  dh: number,
): BoxSpec {
  const isNS = role === "wall_n" || role === "wall_s";
  let cx: number, cy: number;
  switch (role) {
    case "wall_n":
      cx = dw * 0.5;
      cy = -dt * 0.5;
      break; // into room = −Y
    case "wall_s":
      cx = dw * 0.5;
      cy = dt * 0.5;
      break; // into room = +Y
    case "wall_e":
      cx = -dt * 0.5;
      cy = dw * 0.5;
      break; // into room = −X
    case "wall_w":
      cx = dt * 0.5;
      cy = dw * 0.5;
      break; // into room = +X
  }
  return {
    cx,
    cy,
    cz: dh * 0.5,
    sx: isNS ? dw : dt,
    sy: isNS ? dt : dw,
    sz: dh,
  };
}

/**
 * Lever handle geometry in door-panel-local space (same frame as
 * `doorPanelBoxSpec`). Consists of two boxes:
 *   1. Escutcheon — a small square backplate flush on the room-facing door surface.
 *   2. Lever — a horizontal bar extending along the panel toward the hinge.
 *
 * US standard handle height: 36 in (0.914 m) from finished floor.
 * Latch-side inset: ~65 mm from the free (non-hinge) edge.
 * The lever extends 105 mm toward the hinge from the escutcheon centre.
 */
function doorHandleBoxSpecs(role: WallRole, dw: number, dt: number): BoxSpec[] {
  const hz = 0.914; // grip centre height from floor
  const inset = 0.065; // tangent distance from free edge to handle centre
  const plateDiam = 0.055; // escutcheon face size (square)
  const plateThick = 0.008; // escutcheon depth proud of door face
  const leverLen = 0.105; // lever length along tangent (toward hinge)
  const leverDiam = 0.018; // lever cross-section

  // Tangent-axis position of the handle — near the free edge (x=dw for N/S, y=dw for E/W)
  const tPos = dw - inset;

  switch (role) {
    case "wall_n": {
      // Room face at y = −dt; handle proud of that face toward −Y
      const faceNorm = -dt;
      return [
        {
          cx: tPos,
          cy: faceNorm - plateThick * 0.5,
          cz: hz,
          sx: plateDiam,
          sy: plateThick,
          sz: plateDiam,
        },
        {
          cx: tPos - leverLen * 0.5,
          cy: faceNorm - plateThick - leverDiam * 0.5,
          cz: hz,
          sx: leverLen,
          sy: leverDiam,
          sz: leverDiam,
        },
      ];
    }
    case "wall_s": {
      // Room face at y = +dt; handle proud toward +Y
      const faceNorm = dt;
      return [
        {
          cx: tPos,
          cy: faceNorm + plateThick * 0.5,
          cz: hz,
          sx: plateDiam,
          sy: plateThick,
          sz: plateDiam,
        },
        {
          cx: tPos - leverLen * 0.5,
          cy: faceNorm + plateThick + leverDiam * 0.5,
          cz: hz,
          sx: leverLen,
          sy: leverDiam,
          sz: leverDiam,
        },
      ];
    }
    case "wall_e": {
      // Room face at x = −dt; tangent is Y; handle proud toward −X
      const faceNorm = -dt;
      return [
        {
          cx: faceNorm - plateThick * 0.5,
          cy: tPos,
          cz: hz,
          sx: plateThick,
          sy: plateDiam,
          sz: plateDiam,
        },
        {
          cx: faceNorm - plateThick - leverDiam * 0.5,
          cy: tPos - leverLen * 0.5,
          cz: hz,
          sx: leverDiam,
          sy: leverLen,
          sz: leverDiam,
        },
      ];
    }
    case "wall_w": {
      // Room face at x = +dt; tangent is Y; handle proud toward +X
      const faceNorm = dt;
      return [
        {
          cx: faceNorm + plateThick * 0.5,
          cy: tPos,
          cz: hz,
          sx: plateThick,
          sy: plateDiam,
          sz: plateDiam,
        },
        {
          cx: faceNorm + plateThick + leverDiam * 0.5,
          cy: tPos - leverLen * 0.5,
          cz: hz,
          sx: leverDiam,
          sy: leverLen,
          sz: leverDiam,
        },
      ];
    }
  }
}

/**
 * Local transform for a door panel child. The child origin is placed at the
 * hinge point in room space; rotating around Z (at the origin) swings the
 * door open.
 *
 * Rotation sign:
 *   N/W walls — negative Z rotation opens toward room interior.
 *   S/E walls — positive Z rotation opens toward room interior.
 */
function doorPanelTransform(
  role: WallRole,
  faceWidth: number,
  clampedPos: number,
  dw: number,
  width: number,
  depth: number,
  t: number,
  openAngleRad: number,
): GeneratorLocalTransform {
  // Hinge is at the left edge of the doorway (lower tangent-axis value).
  const tangentCentre = faceXToRoom(clampedPos * faceWidth, faceWidth);
  const hingeT = tangentCentre - dw * 0.5;
  const innerFace = wallInnerFaceOffset(role, width, depth, t);
  const rotSign = role === "wall_n" || role === "wall_w" ? -1 : 1;
  let px: number, py: number;
  if (role === "wall_n" || role === "wall_s") {
    px = hingeT;
    py = innerFace;
  } else {
    px = innerFace;
    py = hingeT;
  }
  return {
    position: [px, py, 0],
    rotation: [0, 0, rotSign * openAngleRad],
    scale: [1, 1, 1],
  };
}

// ---------------------------------------------------------------------------
// Door frame helpers
// ---------------------------------------------------------------------------

/**
 * Three-piece door frame (left jamb, right jamb, head casing) on the inner
 * wall face. No bottom casing — doorways extend to the floor.
 * Returns BoxSpecs in room-local space.
 */
function doorFrameBoxSpecs(
  tangent: WallTangent,
  faceWidth: number,
  clampedPos: number,
  dw: number,
  dh: number,
  innerFace: number,
  ft: number,
): BoxSpec[] {
  const cx = clampedPos * faceWidth; // face-space door centre (horizontal)
  const x0 = cx - dw * 0.5;
  const x1 = cx + dw * 0.5;
  const leftJamb = faceBoxSpec(
    tangent,
    innerFace,
    faceWidth,
    x0 - ft,
    x0,
    0,
    dh,
    ft,
  );
  const rightJamb = faceBoxSpec(
    tangent,
    innerFace,
    faceWidth,
    x1,
    x1 + ft,
    0,
    dh,
    ft,
  );
  const head = faceBoxSpec(
    tangent,
    innerFace,
    faceWidth,
    x0 - ft,
    x1 + ft,
    dh,
    dh + ft,
    ft,
  );
  return [leftJamb, rightJamb, head];
}

// ---------------------------------------------------------------------------
// Window frame helpers
// ---------------------------------------------------------------------------

/**
 * Four-piece window frame (left stile, right stile, top rail, bottom rail)
 * on the inner wall face. Returns BoxSpecs in room-local space.
 */
function windowFrameBoxSpecs(
  tangent: WallTangent,
  faceWidth: number,
  clampedPos: number,
  clampedV: number,
  wallHeight: number,
  dw: number,
  dh: number,
  innerFace: number,
  fw: number,
): BoxSpec[] {
  const cx = clampedPos * faceWidth;
  const cz = clampedV * wallHeight;
  const x0 = cx - dw * 0.5;
  const x1 = cx + dw * 0.5;
  const z0 = cz - dh * 0.5;
  const z1 = cz + dh * 0.5;
  const left = faceBoxSpec(
    tangent,
    innerFace,
    faceWidth,
    x0 - fw,
    x0,
    z0 - fw,
    z1 + fw,
    fw,
  );
  const right = faceBoxSpec(
    tangent,
    innerFace,
    faceWidth,
    x1,
    x1 + fw,
    z0 - fw,
    z1 + fw,
    fw,
  );
  const top = faceBoxSpec(
    tangent,
    innerFace,
    faceWidth,
    x0,
    x1,
    z1,
    z1 + fw,
    fw,
  );
  const bottom = faceBoxSpec(
    tangent,
    innerFace,
    faceWidth,
    x0,
    x1,
    z0 - fw,
    z0,
    fw,
  );
  return [left, right, top, bottom];
}

/**
 * Window sill — a horizontal ledge projecting from the inner wall face below
 * the window opening. Its horizontal extent matches the outer frame width
 * (opening ± one frame member on each side). `sillDepth` is the room-inward
 * projection, which is typically deeper than the frame member depth so the
 * sill reads as a distinct ledge rather than just another frame strip.
 */
function sillBoxSpec(
  tangent: WallTangent,
  innerFace: number,
  faceWidth: number,
  clampedPos: number,
  wallHeight: number,
  dw: number,
  dh: number,
  clampedV: number,
  fw: number,
  sillDepth: number,
  sillThick: number,
): BoxSpec {
  const cx = clampedPos * faceWidth;
  const cz = clampedV * wallHeight;
  const z0 = cz - dh * 0.5; // bottom edge of the window opening
  return faceBoxSpec(
    tangent,
    innerFace,
    faceWidth,
    cx - dw * 0.5 - fw,
    cx + dw * 0.5 + fw, // spans outer frame width
    z0 - sillThick,
    z0, // sits immediately below opening
    sillDepth,
  );
}

/**
 * Window muntin bars — one or two glazing bars dividing the pane into equal
 * sections, centred in the opening. Bars use a square cross-section
 * (`muntinWidth` × `muntinWidth`).
 *
 *   "vertical"   → single bar at the horizontal centre
 *   "horizontal" → single bar at the vertical centre
 *   "cross"      → both
 */
function muntinBoxSpecs(
  paneStyle: string,
  tangent: WallTangent,
  innerFace: number,
  faceWidth: number,
  clampedPos: number,
  clampedV: number,
  wallHeight: number,
  dw: number,
  dh: number,
  mw: number,
): BoxSpec[] {
  const cx = clampedPos * faceWidth;
  const cz = clampedV * wallHeight;
  const x0 = cx - dw * 0.5;
  const x1 = cx + dw * 0.5;
  const z0 = cz - dh * 0.5;
  const z1 = cz + dh * 0.5;
  const boxes: BoxSpec[] = [];
  if (paneStyle === "vertical" || paneStyle === "cross") {
    boxes.push(
      faceBoxSpec(
        tangent,
        innerFace,
        faceWidth,
        cx - mw * 0.5,
        cx + mw * 0.5,
        z0,
        z1,
        mw,
      ),
    );
  }
  if (paneStyle === "horizontal" || paneStyle === "cross") {
    boxes.push(
      faceBoxSpec(
        tangent,
        innerFace,
        faceWidth,
        x0,
        x1,
        cz - mw * 0.5,
        cz + mw * 0.5,
        mw,
      ),
    );
  }
  return boxes;
}

// ---------------------------------------------------------------------------
// Parameter schema
// ---------------------------------------------------------------------------

const ROOM_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "room-dimensions",
    label: "Dimensions",
    params: [
      {
        id: "width",
        label: "Width",
        type: "number",
        unit: "meters",
        default: 4,
        min: 0.5,
        max: 50,
        step: 0.1,
      },
      {
        id: "depth",
        label: "Depth",
        type: "number",
        unit: "meters",
        default: 4,
        min: 0.5,
        max: 50,
        step: 0.1,
      },
      {
        id: "height",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 2.5,
        min: 0.5,
        max: 20,
        step: 0.1,
      },
      {
        id: "wallThickness",
        label: "Wall Thickness",
        type: "number",
        unit: "meters",
        default: 0.1,
        min: 0.01,
        max: 1,
        step: 0.01,
      },
      { id: "hasCeiling", label: "Ceiling", type: "boolean", default: false },
    ],
  },
  {
    type: "section",
    id: "room-trim",
    label: "Trim",
    params: [
      {
        id: "hasCrownMolding",
        label: "Crown Molding",
        type: "boolean",
        default: false,
      },
      {
        id: "crownMoldingSize",
        label: "Crown Size",
        type: "number",
        unit: "meters",
        default: 0.07,
        min: 0.01,
        max: 0.3,
        step: 0.005,
        showIf: { id: "hasCrownMolding", value: true },
      },
      {
        id: "hasBaseboard",
        label: "Baseboard",
        type: "boolean",
        default: false,
      },
      {
        id: "baseboardHeight",
        label: "Baseboard Height",
        type: "number",
        unit: "meters",
        default: 0.09,
        min: 0.01,
        max: 0.5,
        step: 0.005,
        showIf: { id: "hasBaseboard", value: true },
      },
      {
        id: "baseboardThickness",
        label: "Baseboard Thickness",
        type: "number",
        unit: "meters",
        default: 0.012,
        min: 0.005,
        max: 0.1,
        step: 0.001,
        showIf: { id: "hasBaseboard", value: true },
      },
    ],
  },
  {
    id: "details",
    label: "Details",
    type: "list",
    itemTypes: ROOM_DETAIL_TYPES,
    default: [],
  },
];

function localTransform(
  pos: [number, number, number],
): GeneratorLocalTransform {
  return { position: pos, rotation: [0, 0, 0], scale: [1, 1, 1] };
}

// ---------------------------------------------------------------------------
// Room generator
// ---------------------------------------------------------------------------

export const ROOM_GENERATOR: Generator = defineGenerator<RoomParams>({
  id: "room",
  label: "Room",
  category: "environment",
  paramSpecs: ROOM_SPECS,
  // Walls are always emitted; users toggle them via the global visibility
  // map so per-wall materials and selection survive show/hide cycles.
  // Each wall drags its crown/baseboard and any doorways/windows on it
  // along via `dependentRoles`. Ceiling is intentionally not listed —
  // it uses the `hasCeiling` topology toggle because rooms with no
  // ceiling are common enough that not generating the geometry is the
  // right default.
  hideableParts: ROOM_HIDEABLE_PARTS,
  defaultParams: {
    width: 4,
    depth: 4,
    height: 2.5,
    wallThickness: 0.1,
    hasCeiling: false,
    hasCrownMolding: false,
    crownMoldingSize: 0.07,
    hasBaseboard: false,
    baseboardHeight: 0.09,
    baseboardThickness: 0.012,
    details: [],
  },
  build(p: RoomParams, _ctx: GeneratorBuildContext): GeneratorOutput {
    const width = numParam(p, "width", 4);
    const depth = numParam(p, "depth", 4);
    const height = numParam(p, "height", 2.5);
    const t = numParam(p, "wallThickness", 0.1);
    const hasCeiling = boolParam(p, "hasCeiling", false);
    const hasCrownMolding = boolParam(p, "hasCrownMolding", false);
    const crownSize = numParam(p, "crownMoldingSize", 0.07);
    const hasBaseboard = boolParam(p, "hasBaseboard", false);
    const bbHeight = numParam(p, "baseboardHeight", 0.09);
    const bbThickness = numParam(p, "baseboardThickness", 0.012);
    const details = listParam(p, "details");

    // Inner wall spans: the walls sit *inside* the floor footprint so the
    // outer wall faces align with the floor edges. This makes Room a closed
    // volume regardless of wall thickness. The +X and -X walls use the
    // inner depth so they don't overlap the +Y and -Y walls at the corners.
    const innerD = Math.max(0.001, depth - 2 * t);

    // Wall face widths: the length along each wall's tangent axis.
    const faceWidthFor = (role: WallRole): number =>
      role === "wall_n" || role === "wall_s" ? width : innerD;

    // Expand array items: a detail with count > 1 becomes N virtual details,
    // each with a computed `position`. The original `position` becomes the
    // centre of the array; items are spaced `gap` metres apart.
    const expandedDetails = details.flatMap((detail) => {
      const count = intParam(detail.params, "count", 1);
      if (count <= 1) return [detail];
      const wall = enumParam(detail.params, "wall", "wall_n");
      const fw = isWallRole(wall) ? faceWidthFor(wall) : width;
      const w = numParam(
        detail.params,
        "width",
        detail.itemType === "doorway" ? 0.9 : 1.0,
      );
      const gap = numParam(detail.params, "gap", 0.3);
      const totalSpan = count * w + (count - 1) * gap;
      const rawStart =
        numParam(detail.params, "position", 0.5) * fw - totalSpan / 2;
      // Clamp so the array stays within the wall face when it fits.
      const startX = Math.max(0, Math.min(fw - totalSpan, rawStart));
      return Array.from({ length: count }, (_, i) => ({
        ...detail,
        id: `${detail.id}_${i}`,
        params: {
          ...detail.params,
          position: (startX + i * (w + gap) + w / 2) / fw,
        },
      }));
    });

    // Bucket details by target wall and resolve each into a face-space hole.
    const holesByWall = new Map<WallRole, Rect2[]>();
    for (const role of WALL_ROLES) holesByWall.set(role, []);
    for (const detail of expandedDetails) {
      const wall = enumParam(detail.params, "wall", "wall_n");
      if (!isWallRole(wall)) continue;
      const hole = holeForDetail(detail, faceWidthFor(wall), height);
      if (hole) holesByWall.get(wall)!.push(hole);
    }

    const wallGeometry = (role: WallRole): PrimitiveGeometry =>
      buildWallMesh(
        wallTangent(role),
        faceWidthFor(role),
        height,
        t,
        holesByWall.get(role) ?? [],
      );

    const children: GeneratorChild[] = [
      // Floor — full width/depth, thickness t. Top face at Z = 0.
      {
        roleId: "floor",
        name: "Floor",
        objectKind: "cube",
        geometry: buildBox(width, depth, t),
        localTransform: localTransform([0, 0, -t * 0.5]),
      },
      {
        roleId: "wall_n",
        name: "Wall N",
        objectKind: "cube",
        geometry: wallGeometry("wall_n"),
        localTransform: localTransform([
          0,
          depth * 0.5 - t * 0.5,
          height * 0.5,
        ]),
      },
      {
        roleId: "wall_s",
        name: "Wall S",
        objectKind: "cube",
        geometry: wallGeometry("wall_s"),
        localTransform: localTransform([
          0,
          -(depth * 0.5 - t * 0.5),
          height * 0.5,
        ]),
      },
      {
        roleId: "wall_e",
        name: "Wall E",
        objectKind: "cube",
        geometry: wallGeometry("wall_e"),
        localTransform: localTransform([
          width * 0.5 - t * 0.5,
          0,
          height * 0.5,
        ]),
      },
      {
        roleId: "wall_w",
        name: "Wall W",
        objectKind: "cube",
        geometry: wallGeometry("wall_w"),
        localTransform: localTransform([
          -(width * 0.5 - t * 0.5),
          0,
          height * 0.5,
        ]),
      },
    ];

    if (hasCeiling) {
      children.push({
        roleId: "ceiling",
        name: "Ceiling",
        objectKind: "cube",
        geometry: buildBox(width, depth, t),
        localTransform: localTransform([0, 0, height + t * 0.5]),
      });
    }

    // Crown molding — one strip per wall at the ceiling line.
    if (hasCrownMolding) {
      for (const role of WALL_ROLES) {
        children.push({
          roleId: `crown_${role.slice(5)}`,
          name: `Crown ${role.slice(5).toUpperCase()}`,
          objectKind: "cube",
          geometry: concatBoxes([
            crownBoxSpec(role, width, depth, innerD, height, t, crownSize),
          ]),
          localTransform: localTransform([0, 0, 0]),
        });
      }
    }

    // Baseboard — one strip per wall at the floor line.
    if (hasBaseboard) {
      for (const role of WALL_ROLES) {
        children.push({
          roleId: `base_${role.slice(5)}`,
          name: `Base ${role.slice(5).toUpperCase()}`,
          objectKind: "cube",
          geometry: concatBoxes(
            baseboardBoxSpecs(
              role,
              width,
              depth,
              innerD,
              t,
              bbHeight,
              bbThickness,
              holesByWall.get(role) ?? [],
            ),
          ),
          localTransform: localTransform([0, 0, 0]),
        });
      }
    }

    // Per-detail geometry — door panels/frames and window frames.
    for (const detail of expandedDetails) {
      const wallStr = enumParam(detail.params, "wall", "wall_n");
      if (!isWallRole(wallStr)) continue;
      const role = wallStr;
      const tang = wallTangent(role);
      const faceWidth = faceWidthFor(role);
      const innerFace = wallInnerFaceOffset(role, width, depth, t);
      const clampedPos = Math.max(
        0,
        Math.min(1, numParam(detail.params, "position", 0.5)),
      );

      if (detail.itemType === "doorway") {
        const dw = numParam(detail.params, "width", 0.9);
        const dh = Math.min(height, numParam(detail.params, "height", 2.1));
        const dt = numParam(detail.params, "doorThickness", 0.04);
        const ft = numParam(detail.params, "frameThickness", 0.05);

        if (boolParam(detail.params, "hasPanel", false)) {
          const openAngleRad =
            numParam(detail.params, "openAngle", 0) * (Math.PI / 180);
          const handleBoxes = boolParam(detail.params, "hasHandle", false)
            ? doorHandleBoxSpecs(role, dw, dt)
            : [];
          children.push({
            roleId: `door_panel_${detail.id}`,
            name: "Door Panel",
            objectKind: "cube",
            geometry: concatBoxes([
              doorPanelBoxSpec(role, dw, dt, dh),
              ...handleBoxes,
            ]),
            localTransform: doorPanelTransform(
              role,
              faceWidth,
              clampedPos,
              dw,
              width,
              depth,
              t,
              openAngleRad,
            ),
          });
        }

        if (boolParam(detail.params, "hasFrame", false)) {
          children.push({
            roleId: `door_frame_${detail.id}`,
            name: "Door Frame",
            objectKind: "cube",
            geometry: concatBoxes(
              doorFrameBoxSpecs(
                tang,
                faceWidth,
                clampedPos,
                dw,
                dh,
                innerFace,
                ft,
              ),
            ),
            localTransform: localTransform([0, 0, 0]),
          });
        }
      }

      if (detail.itemType === "window") {
        const dw = numParam(detail.params, "width", 1.0);
        const dh = numParam(detail.params, "height", 1.2);
        const fw = numParam(detail.params, "frameWidth", 0.05);
        const clampedV = Math.max(
          0,
          Math.min(1, numParam(detail.params, "verticalPosition", 0.5)),
        );
        const hasFrame = boolParam(detail.params, "hasFrame", false);
        const hasSill = hasFrame && boolParam(detail.params, "hasSill", false);
        const paneStyle = enumParam(detail.params, "paneStyle", "none");

        if (hasFrame) {
          const sillBoxes = hasSill
            ? [
                sillBoxSpec(
                  tang,
                  innerFace,
                  faceWidth,
                  clampedPos,
                  height,
                  dw,
                  dh,
                  clampedV,
                  fw,
                  numParam(detail.params, "sillDepth", 0.15),
                  numParam(detail.params, "sillThickness", 0.04),
                ),
              ]
            : [];
          children.push({
            roleId: `window_frame_${detail.id}`,
            name: "Window Frame",
            objectKind: "cube",
            geometry: concatBoxes([
              ...windowFrameBoxSpecs(
                tang,
                faceWidth,
                clampedPos,
                clampedV,
                height,
                dw,
                dh,
                innerFace,
                fw,
              ),
              ...sillBoxes,
            ]),
            localTransform: localTransform([0, 0, 0]),
          });
        }

        if (paneStyle !== "none") {
          const mw = numParam(detail.params, "muntinWidth", 0.04);
          children.push({
            roleId: `window_pane_${detail.id}`,
            name: "Window Muntins",
            objectKind: "cube",
            geometry: concatBoxes(
              muntinBoxSpecs(
                paneStyle,
                tang,
                innerFace,
                faceWidth,
                clampedPos,
                clampedV,
                height,
                dw,
                dh,
                mw,
              ),
            ),
            localTransform: localTransform([0, 0, 0]),
          });
        }
      }
    }

    return {
      kind: "group",
      parent: { name: "Room" },
      children,
    };
  },
});
